import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Auto-detect domain: use env variable, fallback to current window origin
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const defaultTheme = {
  colors: {
    primary: '#1a365d',
    primaryLight: '#2a4a7f',
    primaryDark: '#0f2342',
    accent: '#00d4aa',
    accentHover: '#00b38f',
    accentLight: '#e6fbf6',
    alert: '#f59e0b',
    alertLight: '#fef3c7',
  },
  logo: {
    lightUrl: '',
    darkUrl: '',
    useLight: false,
  },
  agency: {
    name: 'MSC L\'AGENCE',
    avatar: '',
  },
  schedule: {
    enabled: true,
    regular: {
      monday: { open: '09:00', close: '18:00', closed: false },
      tuesday: { open: '09:00', close: '18:00', closed: false },
      wednesday: { open: '09:00', close: '18:00', closed: false },
      thursday: { open: '09:00', close: '18:00', closed: false },
      friday: { open: '09:00', close: '18:00', closed: false },
      saturday: { open: '10:00', close: '13:00', closed: false },
      sunday: { open: '', close: '', closed: true },
    },
    specialDates: [],
    holidayMessage: 'Fermé pour les fêtes',
  },
  seasonal: {
    enabled: false,
    current: 'none',
    avatarDecoration: false,
  }
};

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);
  // Mode: 'agence' (light/default) or 'copilote' (dark/ROI focused)
  const [appMode, setAppMode] = useState(() => {
    return localStorage.getItem('msc_app_mode') || 'agence';
  });

  // Apply mode to body class for global styling
  useEffect(() => {
    document.body.classList.remove('mode-agence', 'mode-copilote');
    document.body.classList.add(`mode-${appMode}`);
    localStorage.setItem('msc_app_mode', appMode);
  }, [appMode]);

  const toggleAppMode = () => {
    setAppMode(prev => prev === 'agence' ? 'copilote' : 'agence');
  };

  const applyThemeToCSS = useCallback((colors) => {
    if (!colors) return;
    const root = document.documentElement;
    root.style.setProperty('--color-primary', colors.primary || '#1a365d');
    root.style.setProperty('--color-primary-light', colors.primaryLight || '#2a4a7f');
    root.style.setProperty('--color-primary-dark', colors.primaryDark || '#0f2342');
    root.style.setProperty('--color-accent', colors.accent || '#00d4aa');
    root.style.setProperty('--color-accent-hover', colors.accentHover || '#00b38f');
    root.style.setProperty('--color-accent-light', colors.accentLight || '#e6fbf6');
    root.style.setProperty('--color-alert', colors.alert || '#f59e0b');
    root.style.setProperty('--color-alert-light', colors.alertLight || '#fef3c7');
  }, []);

  useEffect(() => {
    loadTheme();
  }, []);

  useEffect(() => {
    applyThemeToCSS(theme.colors);
  }, [theme.colors, applyThemeToCSS]);

  const loadTheme = async () => {
    try {
      const response = await axios.get(`${API}/settings/theme`);
      if (response.data && response.data.colors) {
        const loadedTheme = {
          ...defaultTheme,
          ...response.data,
          colors: { ...defaultTheme.colors, ...response.data.colors },
          schedule: { ...defaultTheme.schedule, ...response.data.schedule },
          seasonal: { ...defaultTheme.seasonal, ...response.data.seasonal },
          logo: { ...defaultTheme.logo, ...response.data.logo },
        };
        setTheme(loadedTheme);
        applyThemeToCSS(loadedTheme.colors);
      }
    } catch (error) {
      console.log('Using default theme');
      applyThemeToCSS(defaultTheme.colors);
    } finally {
      setLoading(false);
    }
  };

  const updateTheme = async (newTheme) => {
    const updatedTheme = { 
      ...theme, 
      ...newTheme,
      colors: newTheme.colors ? { ...theme.colors, ...newTheme.colors } : theme.colors,
    };
    setTheme(updatedTheme);
    applyThemeToCSS(updatedTheme.colors);
    
    try {
      await axios.put(`${API}/settings/theme`, updatedTheme);
      return true;
    } catch (error) {
      console.error('Error saving theme:', error);
      return false;
    }
  };

  const updateColors = async (colors) => {
    const newColors = { ...theme.colors, ...colors };
    setTheme(prev => ({ ...prev, colors: newColors }));
    applyThemeToCSS(newColors);
    
    try {
      await axios.put(`${API}/settings/theme`, { ...theme, colors: newColors });
      return true;
    } catch (error) {
      console.error('Error saving colors:', error);
      return false;
    }
  };

  const updateLogo = async (logo) => {
    const newLogo = { ...theme.logo, ...logo };
    setTheme(prev => ({ ...prev, logo: newLogo }));
    
    try {
      await axios.put(`${API}/settings/theme`, { ...theme, logo: newLogo });
      return true;
    } catch (error) {
      console.error('Error saving logo:', error);
      return false;
    }
  };

  const updateSchedule = async (schedule) => {
    const newSchedule = { ...theme.schedule, ...schedule };
    setTheme(prev => ({ ...prev, schedule: newSchedule }));
    
    try {
      await axios.put(`${API}/settings/theme`, { ...theme, schedule: newSchedule });
      return true;
    } catch (error) {
      console.error('Error saving schedule:', error);
      return false;
    }
  };

  const updateSeasonal = async (seasonal) => {
    const newSeasonal = { ...theme.seasonal, ...seasonal };
    setTheme(prev => ({ ...prev, seasonal: newSeasonal }));
    
    try {
      await axios.put(`${API}/settings/theme`, { ...theme, seasonal: newSeasonal });
      return true;
    } catch (error) {
      console.error('Error saving seasonal:', error);
      return false;
    }
  };

  const getCurrentScheduleStatus = useCallback(() => {
    if (!theme.schedule?.enabled) {
      return { isOpen: true, message: '' };
    }
    
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[now.getDay()];
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    // Check special dates first
    const todayStr = now.toISOString().split('T')[0];
    const specialDate = theme.schedule.specialDates?.find(d => d.date === todayStr);
    if (specialDate) {
      return {
        isOpen: !specialDate.closed,
        message: specialDate.label,
        isSpecial: true
      };
    }
    
    const daySchedule = theme.schedule.regular?.[currentDay];
    if (!daySchedule || daySchedule.closed) {
      return { isOpen: false, message: 'Fermé aujourd\'hui', nextOpen: getNextOpenTime() };
    }
    
    const isOpen = currentTime >= daySchedule.open && currentTime < daySchedule.close;
    
    if (isOpen) {
      return { 
        isOpen: true, 
        message: `Ouvert jusqu'à ${daySchedule.close}`,
        closesAt: daySchedule.close
      };
    } else if (currentTime < daySchedule.open) {
      return { 
        isOpen: false, 
        message: `Ouvre à ${daySchedule.open}`,
        opensAt: daySchedule.open
      };
    } else {
      return { 
        isOpen: false, 
        message: 'Fermé',
        nextOpen: getNextOpenTime()
      };
    }
  }, [theme.schedule]);

  const getNextOpenTime = useCallback(() => {
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayLabels = {
      sunday: 'Dimanche',
      monday: 'Lundi',
      tuesday: 'Mardi',
      wednesday: 'Mercredi',
      thursday: 'Jeudi',
      friday: 'Vendredi',
      saturday: 'Samedi'
    };
    
    for (let i = 1; i <= 7; i++) {
      const nextDay = new Date(now);
      nextDay.setDate(now.getDate() + i);
      const dayName = dayNames[nextDay.getDay()];
      const schedule = theme.schedule?.regular?.[dayName];
      
      if (schedule && !schedule.closed) {
        return `${dayLabels[dayName]} ${schedule.open}`;
      }
    }
    return null;
  }, [theme.schedule]);

  const getSeasonalDecoration = useCallback(() => {
    if (!theme.seasonal?.enabled || !theme.seasonal?.avatarDecoration) return null;
    
    const decorations = {
      christmas: {
        overlay: '🎅',
        position: 'top-right',
        className: 'christmas-decoration'
      },
      halloween: {
        overlay: '🎃',
        position: 'top-right',
        className: 'halloween-decoration'
      },
      summer: {
        overlay: '🌴',
        position: 'top-right',
        className: 'summer-decoration'
      },
      easter: {
        overlay: '🐰',
        position: 'top-right',
        className: 'easter-decoration'
      },
      none: null
    };
    
    return decorations[theme.seasonal?.current] || null;
  }, [theme.seasonal]);

  const value = {
    theme,
    loading,
    appMode,
    toggleAppMode,
    setAppMode,
    isCopiloteMode: appMode === 'copilote',
    updateTheme,
    updateColors,
    updateLogo,
    updateSchedule,
    updateSeasonal,
    getCurrentScheduleStatus,
    getSeasonalDecoration,
    reloadTheme: loadTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
